import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ShieldCheck,
  ClipboardList, GraduationCap, Eye, Layers, Wallet, Zap,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { RiskReversalBand, FaqSection, StickyExperienceCTA } from "@/components/marketing/ConversionKit";
import { logoHorizontalDark } from "@/assets/brand";
import { LanguageSelector } from "@/components/marketing/LanguageSelector";
import { HeroVideo } from "@/components/marketing/HeroVideo";
import { WorkshopStudio } from "@/components/marketing/workshop/WorkshopStudio";
import { BookingShowcase } from "@/components/marketing/booking/BookingShowcase";
import { AriConversationSection } from "@/components/marketing/AriConversationSection";
import AriOrb from "@/components/ari/AriOrb";
import NetworkField from "@/components/marketing/NetworkField";
import { BentoFeatureCard } from "@/components/marketing/BentoFeatureCard";
import { AmbientBackground } from "@/components/marketing/AmbientBackground";
import { AuthModal } from "@/components/marketing/AuthModal";
import { EcosystemFlow } from "@/components/marketing/EcosystemFlow";
import { TrustBand } from "@/components/marketing/TrustBand";
import { PanelAmbience } from "@/components/marketing/HeroCinematic";
import { WhyChooseUs } from "@/components/marketing/WhyChooseUs";
import { SectionHeading } from "@/components/marketing/SectionHeading";
import { PartnerCTA } from "@/components/marketing/PartnerCTA";
import { OperationsBrainShowcase } from "@/components/marketing/OperationsBrainShowcase";
import { Depth4D } from "@/components/marketing/Depth4D";
import { track } from "@/lib/analytics";

const EASE = [0.22, 1, 0.36, 1];

const SECTORS = ["Fine Dining", "Casual Dining", "Hotels", "Cocktail Bars", "Cafés", "Bakeries", "Pubs", "Quick Service"];

const VALUE_PILLARS = [
  { k: "5", label: "systems replaced", sub: "Rota, training, payroll, comms & reporting — unified" },
  { k: "1", label: "login for everything", sub: "One platform, one team, one source of truth" },
  { k: "£350", label: "per month", sub: "Unlimited staff · every module included" },
  { k: "24/7", label: "ARI on call", sub: "A living intelligence that reads across your whole venue" },
];

const AriGlyph = () => <AriOrb size={20} state="insight" />;
const OUTCOMES = [
  { icon: Layers, title: "One platform instead of five", span: "md:col-span-8", accent: true,
    desc: "Replace disconnected rota, training, payroll, messaging and reporting tools with a single intelligent operating system — connected data, one login, no vendor juggling." },
  { icon: ClipboardList, title: "Reduce admin workload", span: "md:col-span-4",
    desc: "Automate the repetitive work of scheduling, onboarding, receipts and reporting so managers spend time on the floor, not the back office." },
  { icon: GraduationCap, title: "Train staff more efficiently", span: "md:col-span-4",
    desc: "Role-based, game-style learning onboards new starters automatically and keeps standards sharp — no course-building required." },
  { icon: Eye, title: "Improve operational visibility", span: "md:col-span-4",
    desc: "See workforce, training, attendance and spend in one executive view — always current, never scattered across spreadsheets." },
  { icon: AriGlyph, title: "Better decisions with ARI", span: "md:col-span-4",
    desc: "ARI reads across every domain and recommends the next best action — always explained, only acting when you confirm." },
];

export default function Landing() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const openAuth = (m = "login") => { setAuthMode(m); setAuthOpen(true); };

  // "Experience it" opens /experience, which launches the real Public Demo directly.

  const goExperience = () => {
    track("hero_cta", { location: "landing" });
    navigate("/experience");
  };


  return (
    <div className="landing-living min-h-screen bg-[#0A0A0B] text-white overflow-x-clip" data-testid="landing-page">
      <AmbientBackground living />
      <div className="relative z-10">
      {/* Header */}
      <motion.header initial={{ y: -70 }} animate={{ y: 0 }} transition={{ duration: 0.6, ease: EASE }}
        className="relative z-50">
        {/* Cinematic restaurant film playing inside the header, behind the logo/nav (clipped by its own wrapper so it never spills over the dropdown) */}
        <PanelAmbience />
        {/* the film is treated as ambient environment, not a bordered video: an even scrim, a legibility
            pool behind the logo, a warm champagne tie-in that shares the hero's palette, and top+bottom
            feathers so the footage melts into the charcoal environment (no hard video edges) */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(85% 150% at 80% 15%, rgba(212,175,55,0.10), transparent 62%)", mixBlendMode: "soft-light" }} />
        <div className="relative z-[1] max-w-7xl mx-auto flex items-center justify-between h-20 sm:h-[88px] lg:h-24 px-6 sm:px-10 lg:px-12">
          <span className="relative h-8 sm:h-9 flex items-center" data-testid="landing-logo">
            {/* constant legibility pool: keeps the gold ARI emblem + wordmark pristine over ANY scene and through crossfades (never fades) */}
            <span aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ width: "128%", height: "230%", background: "radial-gradient(ellipse 58% 52% at 50% 50%, rgba(7,7,9,0.62), rgba(7,7,9,0.3) 54%, transparent 78%)", filter: "blur(8px)" }} />
            <img src={logoHorizontalDark} alt="Admin Restaurant Intelligence" className="relative h-full w-auto select-none" data-testid="landing-logo-emblem"
              style={{ opacity: 1, filter: "drop-shadow(0 0 1px rgba(0,0,0,0.9)) drop-shadow(0 1px 5px rgba(0,0,0,0.6))" }} />
          </span>
          <div className="flex items-center gap-2 sm:gap-3">
            <LanguageSelector />
            <button onClick={() => openAuth("login")} data-testid="landing-signin"
              className="text-sm font-medium tracking-[0.02em] text-white/85 hover:text-[#D4AF37] rounded-full px-4 py-2 bg-[#101013]/60 border border-white/12 backdrop-blur-md hover:bg-[#D4AF37]/[0.1] hover:border-[#D4AF37]/40 transition-all duration-300 ease-out">{t("Sign in")}</button>
          </div>
        </div>
      </motion.header>

      {/* Hero */}
      <Depth4D recedeOnly strength={0.6}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* charcoal-gold base — no flat black anywhere in the hero section */}
          <div className="absolute inset-0" style={{ background: "#14100A" }} />
          <div className="absolute inset-0 bg-[radial-gradient(900px_400px_at_64%_-8%,rgba(212,175,55,0.14),transparent_70%)]" />
          <div className="absolute inset-x-0 bottom-0 h-56" style={{ background: "linear-gradient(to top, #14100A 0%, rgba(20,16,10,0.6) 55%, transparent 100%)" }} />
          <div className="light-bloom" aria-hidden="true" />
        </div>

        <div className="relative z-[3] isolate max-w-7xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10 pb-8 sm:pb-12 hero-soft-in" data-testid="hero-video-stage">
          <div className="relative z-[3] isolate" data-testid="hero-video-panel">
            <HeroVideo />
          </div>
        </div>

      </section>
      </Depth4D>

      {/* Reservations — a self-playing slideshow of how a real ARI booking flows */}
      <Depth4D strength={0.5}><BookingShowcase /></Depth4D>

      {/* The ARI conversation, on its own stage */}
      <Depth4D strength={0.5}><AriConversationSection /></Depth4D>


      <div className="relative">
        {/* Sector marquee — a brushed metallic-glass band matching the "No card needed" trust band:
            ultra-realistic glass base, diagonal sheen, a living network and a travelling polish sweep. */}
        <div className="relative z-[2] overflow-hidden border-y border-[#D4AF37]/15" data-testid="sector-marquee-band">
          <style>{`@keyframes secSheen{0%{transform:translateX(-120%) skewX(-16deg)}100%{transform:translateX(320%) skewX(-16deg)}}`}</style>
          {/* brushed metallic-glass base */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #16120B 0%, #15110A 50%, #14100A 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(105deg, rgba(246,227,166,0.06) 0%, transparent 18%, transparent 82%, rgba(246,227,166,0.04) 100%)" }} />
          <NetworkField count={16} speed={0.14} linkDist={0.42} style={{ opacity: 0.5 }} />
          {/* travelling polish highlight */}
          <div className="absolute inset-y-0 left-0 w-1/3 pointer-events-none" style={{ background: "linear-gradient(100deg, transparent, rgba(246,227,166,0.10), transparent)", mixBlendMode: "screen", animation: "secSheen 5.5s ease-in-out infinite alternate" }} />
          {/* subtle champagne edge grounding (no white line) */}
          <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 1px 0 rgba(246,227,166,0.05)" }} />
          {/* scrolling sector names (edge-faded) */}
          <div className="relative z-[1] py-5 marquee-mask overflow-hidden">
            <div className="marquee-track" data-testid="sector-marquee">
              {[...SECTORS, ...SECTORS, ...SECTORS].map((s, i) => (
                <span key={i} className="mx-6 text-sm font-mono-lux uppercase tracking-[0.2em] text-white/60 whitespace-nowrap" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}>{s}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ARI Workshop — try building a tool, step by step */}
      <Depth4D strength={0.5}><WorkshopStudio /></Depth4D>

      {/* Trust band */}
      <Depth4D strength={0.5}><TrustBand /></Depth4D>


      {/* Value band — conversion message */}
      <Depth4D>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-14" data-testid="value-band">
        <motion.div className="max-w-3xl mb-8 sm:mb-10"
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: EASE }}>
          <SectionHeading testId="value-heading" eyebrow="Every moment, handled" lead="The venue that" accent="never misses a moment." shimmer />
          <div className="mt-6 flex items-stretch gap-4 max-w-2xl">
            <motion.span aria-hidden className="w-px shrink-0 origin-top" style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.9), rgba(212,175,55,0) 92%)" }}
              initial={{ scaleY: 0 }} whileInView={{ scaleY: 1 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8, ease: EASE, delay: 0.15 }} />
            <p className="text-white/70 text-base sm:text-lg leading-relaxed">{t("From the first reservation to the last goodbye, ARI reads across every table, request and regular — and nudges your team the instant something needs them. Fuller tables, more regulars, fewer no-shows, all in one connected system.")}</p>
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2.5" data-testid="value-pillar-row">
            {["Reservations", "Guest experience", "Service", "Regulars", "Feedback"].map((p, i) => (
              <span key={p} className="inline-flex items-center gap-3">
                {i > 0 && <span className="w-1 h-1 rounded-full bg-white/20" />}
                <span className="text-[11px] sm:text-xs uppercase tracking-[0.16em] text-white/80">{t(p)}</span>
              </span>
            ))}
            <span className="inline-flex items-center gap-2 ml-1 rounded-full pl-2.5 pr-3 py-1 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06]">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "radial-gradient(circle at 34% 28%, #FFF7D6 0%, #F3DC8E 32%, #D4AF37 62%, #9A7B22 100%)", boxShadow: "0 0 8px rgba(212,175,55,0.6)" }} />
              <span className="font-mono-lux text-[10px] sm:text-[10.5px] tracking-[0.16em] uppercase text-[#FBECA1]">{t("ARI")}</span>
            </span>
          </div>
        </motion.div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {VALUE_PILLARS.map((p, i) => (
            <motion.div key={p.label} data-testid="value-pillar" className="lux-card p-6 sm:p-7"
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.08 }}>
              <p className="font-bold tracking-tight text-4xl sm:text-5xl text-[#D4AF37] leading-none">{p.k}</p>
              <p className="text-sm text-white mt-3 font-medium">{t(p.label)}</p>
              <p className="text-xs text-white/45 mt-1.5 leading-relaxed">{t(p.sub)}</p>
            </motion.div>
          ))}
        </div>
      </section>
      </Depth4D>

      {/* Why choose us — comparison */}
      <Depth4D><WhyChooseUs /></Depth4D>
      <RiskReversalBand />

      {/* Ecosystem flow */}
      <Depth4D>
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10 sm:py-14" data-testid="ecosystem-section">
        <motion.div className="max-w-2xl mb-8 sm:mb-10"
          initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: EASE }}>
          <SectionHeading testId="ecosystem-heading" eyebrow="One connected journey" lead="From first shift" accent="to finest service." />
          <p className="text-white/55 mt-5 leading-relaxed">{t("Every step flows into the next. An employee joins once — and the platform carries them through onboarding, training, rota, attendance, performance and pay, while ARI watches over the whole operation.")}</p>
        </motion.div>
        <EcosystemFlow />
      </section>
      </Depth4D>

      {/* ARI Intelligence split */}
      <Depth4D>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-14 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: EASE }}>
          <SectionHeading testId="intelligence-heading" eyebrow="The intelligence behind it" lead="It sees" accent="what others miss." />
          <p className="text-white/60 mt-6 leading-relaxed">
            {t("ARI reads workforce, receipts, training, compliance and performance together — surfacing grounded insights with confidence scores, always explaining its reasoning, and only ever acting when you confirm.")}
          </p>
          <button onClick={goExperience} data-testid="ops-brain-cta"
            className="mt-8 inline-flex items-center gap-2 text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-6 py-3 hover:bg-[#D4AF37]/10 transition-colors duration-200 disabled:opacity-60">
            {t("Experience it")} <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 34 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: EASE }}>
          <OperationsBrainShowcase />
        </motion.div>
      </section>
      </Depth4D>

      {/* Business outcomes bento */}
      <Depth4D strength={0.7}>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-6 pb-10 sm:pb-14" data-testid="business-outcomes">
        <motion.div className="max-w-2xl mb-8 sm:mb-10" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, ease: EASE }}>
          <SectionHeading testId="outcomes-heading" eyebrow="Built for results" lead="Fuller tables. Sharper teams." accent="Stronger margins." />
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          {OUTCOMES.map((f, i) => <BentoFeatureCard key={f.title} {...f} title={t(f.title)} desc={t(f.desc)} index={i} />)}
        </div>
      </section>
      </Depth4D>

      <Depth4D strength={0.5}><PartnerCTA /></Depth4D>

      <Depth4D strength={0.6}><FaqSection /></Depth4D>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/40" data-testid="footer-trust-line">
          <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#D4AF37]" /> {t("No setup required • Explore instantly")}</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {t("Isolated sample data · no sign-up · no real customer information")}</span>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-14 flex flex-col sm:flex-row items-center justify-between gap-8">
          <div className="flex flex-col items-center sm:items-start gap-3">
            <span className="logo-glimmer h-8 opacity-80">
              <img src={logoHorizontalDark} alt="Admin Restaurant Intelligence" className="h-full w-auto" />
              <span className="logo-sheen" style={{ WebkitMaskImage: `url(${logoHorizontalDark})`, maskImage: `url(${logoHorizontalDark})` }} />
            </span>
            <p className="text-xs text-white/35 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> {t("One platform")}<span data-no-translate> · </span><span data-no-translate className="font-bold text-white/70">£350/month</span><span data-no-translate> · </span>{t("unlimited staff")}</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/40">
            <a href="/legal" target="_blank" rel="noopener noreferrer" data-testid="landing-legal" className="hover:text-[#D4AF37] transition-colors duration-200">{t("Legal Centre")}</a>
            <a href="/legal/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors duration-200">{t("Terms")}</a>
            <a href="/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors duration-200">{t("Privacy")}</a>
            <a href="/legal/cookie-policy" target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors duration-200">{t("Cookies")}</a>
          </div>
        </div>
      </footer>

      </div>
      <AuthModal open={authOpen} initialMode={authMode} portal="restaurant" onClose={() => setAuthOpen(false)} />
      <StickyExperienceCTA />
    </div>
  );
}
